import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatAngularModule } from '../../shared/modules/mat-angular.module';
import { Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil, tap, catchError } from 'rxjs/operators';
import { ProjectManagementService, Project } from '../../services/project-management.service';
import { UserManagementService } from '../../services/user-management.service';
import { User } from '../../services/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';

export interface ReassignProjectOwnerDialogData {
  project: Project;
}

@Component({
  selector: 'app-reassign-project-owner-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatAngularModule],
  templateUrl: './reassign-project-owner-dialog.component.html',
  styleUrl: './reassign-project-owner-dialog.component.scss',
})
export class ReassignProjectOwnerDialogComponent implements OnInit, OnDestroy {
  searchControl = new FormControl<string | User>('');
  filteredUsers: User[] = [];
  selectedUser: User | null = null;
  saving = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<ReassignProjectOwnerDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: ReassignProjectOwnerDialogData,
    private projectService: ProjectManagementService,
    private userManagementService: UserManagementService,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        tap((v) => {
          if (typeof v === 'string') {
            this.selectedUser = null;
          }
        }),
        distinctUntilChanged((a, b) => {
          const sa = typeof a === 'string' ? a : a?.email ?? '';
          const sb = typeof b === 'string' ? b : b?.email ?? '';
          return sa === sb;
        }),
        switchMap((value) => {
          if (value !== null && typeof value !== 'string') {
            return of([]);
          }
          const term = String(value ?? '').trim();
          if (term.length < 2) {
            this.filteredUsers = [];
            return of([]);
          }
          return this.userManagementService.searchUsers(term, 25).pipe(
            catchError(() => of([] as User[])),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((users) => {
        this.filteredUsers = users;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  displayUser(user: User | string | null): string {
    if (!user || typeof user === 'string') {
      return typeof user === 'string' ? user : '';
    }
    return `${user.full_name} (${user.email})`;
  }

  onUserSelected(event: MatAutocompleteSelectedEvent): void {
    const u = event.option.value as User;
    this.selectedUser = u;
    this.searchControl.setValue(u, { emitEvent: false });
  }

  close(): void {
    this.dialogRef.close(false);
  }

  submit(): void {
    if (!this.selectedUser || this.saving) {
      return;
    }
    if (this.selectedUser.id === this.data.project.created_by) {
      this.snackBar.open('This user is already the project owner.', 'Close', { duration: 4000 });
      return;
    }
    this.saving = true;
    this.projectService
      .reassignProjectOwner(this.data.project.id, this.selectedUser.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.snackBar.open(`Project owner updated to ${this.selectedUser!.full_name}.`, 'Close', {
            duration: 5000,
          });
          this.dialogRef.close(true);
        },
        error: (err) => {
          this.saving = false;
          let msg = 'Re-assign failed';
          const d = err?.error?.detail;
          if (Array.isArray(d)) {
            msg = d.map((x: { msg?: string }) => x?.msg || JSON.stringify(x)).join('; ');
          } else if (d != null) {
            msg = String(d);
          } else if (err?.message) {
            msg = String(err.message);
          }
          this.snackBar.open(msg, 'Close', { duration: 6000 });
        },
      });
  }
}
